/**
 * Page 4 of the booking flow, the part with no screen (batch-29, item R11; plan §6): the people of the booking as the
 * rooms hold them, what each is called on the page, which of them is the main guest, what the page still needs before
 * it lets the guest go on, and the `persons` list the booking is sent with. Not asked for anywhere in the flow: who a
 * person rooms with (the rooms say it), a person's age, a person's dietary needs (item BG1: they go in the booking's
 * special requests), and every field of the hidden pack. A person's gender is asked
 * only on an event whose organizer turned that question on (item R24, ./gender).
 */
import type { FormFieldDef, PublicAgeGroup, PublicBookingForm } from '../public/api';
import { GENDER_FOR_EVERYONE, genderAnswer, type GenderAsk } from './gender';
import { shownRequestFields, type RequestField } from './requestFields';
import type { DraftPerson, DraftRoom } from './state';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_RE = /^[\d\s\-+()]{7,}$/;

const SINGULAR: Record<string, string> = {
  adults: 'Adult', children: 'Child', kids: 'Kid', infants: 'Infant', babies: 'Baby', toddlers: 'Toddler', teens: 'Teen', teenagers: 'Teenager',
  men: 'Man', women: 'Woman', people: 'Person', seniors: 'Senior', boys: 'Boy', girls: 'Girl', students: 'Student', guests: 'Guest',
};
/** One person of a group, from the group's name: "Adults" → "Adult", "Children" → "Child". A name with no known singular stays as the organizer typed it. */
export function singular(name: string): string {
  const n = name.trim();
  return SINGULAR[n.toLowerCase()] ?? n;
}

/** One person's place in the booking. */
export interface Slot {
  /** The room's position, from 1. */
  room: number;
  group: string;
  /** Which of the room's people of that group this is, from 0: how a kept name finds its person again. */
  nth: number;
  /** "Adult 2", "Child 1": the group's name in the singular and a number per group across the booking. */
  label: string;
  /** The main guest: the first person of the oldest group in room 1. Their name is asked for once, at the top. */
  main: boolean;
}

/** Everyone in the booking, room by room, the oldest group first in each room. */
export function slotsOf(rooms: readonly DraftRoom[], groups: readonly PublicAgeGroup[]): Slot[][] {
  const oldestFirst = [...groups].reverse();
  const seen: Record<string, number> = {};
  let mainGiven = false;
  return rooms.map((r, i) => {
    const out: Slot[] = [];
    for (const g of oldestFirst) {
      const n = r.people[g.key] ?? 0;
      for (let nth = 0; nth < n; nth += 1) {
        seen[g.key] = (seen[g.key] ?? 0) + 1;
        const main = !mainGiven;
        mainGiven = true;
        out.push({ room: i + 1, group: g.key, nth, label: `${singular(g.name)} ${seen[g.key]}`, main });
      }
    }
    return out;
  });
}

const same = (p: DraftPerson, s: Slot) => p.room === s.room && p.group === s.group;
/** What the guest entered for one person: the kept entry of that room and group, by its order among them. */
export function personOf(names: readonly DraftPerson[] | null | undefined, slot: Slot): DraftPerson {
  return (names ?? []).filter((p) => same(p, slot))[slot.nth] ?? { room: slot.room, group: slot.group };
}

const text = (v: string | undefined) => (v ?? '').trim();
/** The kept names after one person's entry changed: one entry per person of the booking, in the page's order, and none for a person who is no longer in it. */
export function withPerson(names: readonly DraftPerson[] | null | undefined, slots: readonly Slot[][], slot: Slot, patch: Partial<Pick<DraftPerson, 'first_name' | 'last_name' | 'gender'>>): DraftPerson[] {
  return slots.flat().map((s) => {
    const was = personOf(names, s);
    const p: DraftPerson = s === slot || (s.room === slot.room && s.group === slot.group && s.nth === slot.nth) ? { ...was, ...patch } : was;
    const out: DraftPerson = { room: s.room, group: s.group };
    if (p.first_name) out.first_name = p.first_name;
    if (p.last_name) out.last_name = p.last_name;
    if (genderAnswer(p.gender)) out.gender = p.gender;
    return out;
  });
}

export interface SentPerson { room: number; group: string; first_name: string | null; last_name: string | null; gender?: 'M' | 'F' | null }
/**
 * The `persons` list a booking is sent with: everyone, in the rooms' order; the main guest carries the name given at the
 * top. Each person carries `gender` only on an event that asks it (`ask`): with the question off the list is what it
 * always was, key for key, whatever an older draft still holds. No person carries a dietary note (item BG1); the server
 * still takes one from an older tab.
 */
export function personsOf(names: readonly DraftPerson[] | null | undefined, slots: readonly Slot[][], details: Record<string, string> | null | undefined, ask?: GenderAsk | null): SentPerson[] {
  return slots.flat().map((s) => {
    const p = personOf(names, s);
    const out: SentPerson = {
      room: s.room, group: s.group,
      first_name: (s.main ? text(details?.first_name) : text(p.first_name)) || null,
      last_name: (s.main ? text(details?.last_name) : text(p.last_name)) || null,
    };
    if (ask) out.gender = genderAnswer(p.gender);
    return out;
  });
}

/** The key a custom question's answer is kept under. */
export const customKey = (id: string): string => `cf_${id}`;

/** What the organizer's ticks put on the page: the fields under "Requests (optional)". Gender is not one of them: it has its own setting on the event (./gender). A dietary tick shows Special requests (./requestFields). */
export function requestsOf(form: PublicBookingForm | null | undefined): { folded: RequestField[] } {
  return { folded: shownRequestFields(form?.request_fields).filter((k) => k !== 'gender') };
}

export const NAME_FOR_EVERYONE = 'Please add a name for everyone.';

/** Where the second typing of the email is kept (item PK3). It is the page's own check: nothing sends it anywhere. */
export const EMAIL_AGAIN = 'email_confirm';
export const EMAILS_DIFFER = 'The two emails are not the same.';
/** The email typed twice the same: spaces around it and capital letters make no difference. */
export const emailsMatch = (email: string | null | undefined, again: string | null | undefined): boolean =>
  (email ?? '').trim().toLowerCase() === (again ?? '').trim().toLowerCase();
/**
 * Why page 4 cannot go on: a message per field that is not in order, keyed by the field (`first_name`, `email`,
 * `email_confirm` when the email typed a second time is not the first, a
 * custom question's `cf_<id>`), `names` when the event requires every name and one is missing, and `gender` when the
 * event requires everyone's gender and one has none. Empty = go on.
 */
export function detailsProblems(
  details: Record<string, string> | null | undefined, names: readonly DraftPerson[] | null | undefined, slots: readonly Slot[][],
  form: PublicBookingForm | null | undefined, questions: readonly FormFieldDef[] | null | undefined, ask?: GenderAsk | null,
): Record<string, string> {
  const d = details ?? {};
  const out: Record<string, string> = {};
  if (!text(d.first_name)) out.first_name = 'Please enter your first name.';
  if (!text(d.last_name)) out.last_name = 'Please enter your last name.';
  if (!EMAIL_RE.test(text(d.email))) out.email = 'Please enter a valid email.';
  // said once there is an email to compare with: an empty pair is the email's own message
  if (text(d.email) && !emailsMatch(d.email, d[EMAIL_AGAIN])) out[EMAIL_AGAIN] = EMAILS_DIFFER;
  if (!PHONE_RE.test(text(d.phone))) out.phone = 'Please enter a valid phone number.';
  if (form?.names_required && slots.flat().some((s) => !s.main && !text(personOf(names, s).first_name))) out.names = NAME_FOR_EVERYONE;
  if (ask === 'required' && slots.flat().some((s) => !genderAnswer(personOf(names, s).gender))) out.gender = GENDER_FOR_EVERYONE;
  for (const q of questions ?? []) if (q.required && !text(d[customKey(q.id)])) out[customKey(q.id)] = `${q.label || 'This field'} is required`;
  return out;
}
